// src/components/molecules/AuthComponent.js
// Flips layout: Google/Apple first, then email. Adds centered divider + “Sign up with email” that carries `from`.

import React, { useState, useRef, useEffect } from "react";
import { Container, Grid, Box, Button, Typography, Link } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import GoogleIcon from "../../assets/google-button-icon.png";
import AppleIcon from "../../assets/apple-button-icon.png";

const AuthComponent = ({
  theme,
  pair,
  pairingCode,
  handleEmailPasswordSignIn,
  handleGoogleSignIn,
  handleAppleSignIn,
  onCodeComplete,
  onForgotPassword,
  loading,
  error,
  notice,
}) => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const isInitialCodeSet = useRef(false);

  // Preserve `from` if present (used by the Sign up link)
  const from = location.state?.from || location;

  useEffect(() => {
    if (pairingCode && !isInitialCodeSet.current) {
      const upper = pairingCode.toUpperCase();
      const newCode = upper.slice(0, 6).split("");
      setCode(newCode);
      const firstEmpty = newCode.findIndex((d) => d === "");
      if (firstEmpty !== -1 && inputRefs.current[firstEmpty]) {
        inputRefs.current[firstEmpty].focus();
      }
      isInitialCodeSet.current = true;
    }
  }, [pairingCode]);

  useEffect(() => {
    if (code.every((d) => d !== "")) onCodeComplete(code.join(""));
  }, [code, onCodeComplete]);

  const handleCodeInput = (i, v) => {
    const regex = /^[a-zA-Z0-9]$/;
    if (!regex.test(v) && v.length === 1) return;
    const next = [...code];
    next[i] = v.toUpperCase();
    setCode(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handlePaste = (i, e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").slice(0, 6);
    const next = [...code];
    pasted.split("").forEach((ch, idx) => {
      if (i + idx < 6) next[i + idx] = ch.toUpperCase();
    });
    setCode(next);
    const last = Math.min(i + pasted.length - 1, 5);
    inputRefs.current[last]?.focus();
  };

  const handleBackspace = (i) => {
    const next = [...code];
    next[i] = "";
    setCode(next);
    if (i > 0) inputRefs.current[i - 1].focus();
  };

  // Reusable thin divider line
  const Line = ({ mx = 2 }) => (
    <Box
      sx={{
        flex: 1,
        height: 1,
        mx,
        backgroundColor: "rgba(255,255,255,0.15)",
      }}
    />
  );

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontFamily: "basic-sans", fontWeight: "bold", fontSize: "1.3rem" }}
          mb={3}
          align="center"
        >
          {pair ? "Enter TV pairing code & Sign in" : "Sign in with"}
        </Typography>

        {error && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mb: 1, fontWeight: "bold", color: theme.colors.red }}
          >
            {error}
          </Typography>
        )}
        {notice && (
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: "bold", color: theme.colors.green }}
          >
            {notice}
          </Typography>
        )}

        {pair && (
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" gap={1} mb={4} width="100%">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeInput(index, e.target.value)}
                    onKeyDown={(e) => e.key === "Backspace" && handleBackspace(index)}
                    onPaste={(e) => handlePaste(index, e)}
                    ref={(el) => (inputRefs.current[index] = el)}
                    style={{
                      width: "19%",
                      height: "60px",
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontFamily: "basic-sans",
                      fontWeight: "700",
                      color: "white",
                      backgroundColor: theme.colors.mediumPurple,
                      borderRadius: "12px",
                      border: 0,
                      outline: "none",
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Social auth FIRST */}
        <Box mt={1} mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                onClick={handleGoogleSignIn}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: theme.colors.paleBlue,
                  border: `1px solid ${theme.colors.paleBlue}`,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "10px 30px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  boxShadow: "none",
                  position: "relative",
                  "&:hover": {
                    border: `1px solid ${theme.colors.darkGray}`,
                    backgroundColor: theme.colors.darkBlue,
                    boxShadow: "none",
                  },
                }}
                disabled={loading}
              >
                <img
                  src={GoogleIcon}
                  alt="Google Icon"
                  style={{ width: "20px", position: "absolute", left: "16px" }}
                />
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  {loading ? "Signing In..." : "Sign In with Google"}
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={handleAppleSignIn}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: theme.colors.palePurple,
                  border: `1px solid ${theme.colors.palePurple}`,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "10px 30px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  boxShadow: "none",
                  position: "relative",
                  "&:hover": {
                    border: `1px solid ${theme.colors.darkGray}`,
                    backgroundColor: theme.colors.darkBlue,
                    boxShadow: "none",
                  },
                }}
                disabled={loading}
              >
                <img
                  src={AppleIcon}
                  alt="Apple Icon"
                  style={{ width: "20px", position: "absolute", left: "16px" }}
                />
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  {loading ? "Signing In..." : "Sign In with Apple"}
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Centered divider */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          <Line />
          <Typography
            variant="body2"
            sx={{
              fontFamily: "basic-sans",
              fontWeight: 700,
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
            }}
          >
            or sign in with your email
          </Typography>
          <Line />
        </Box>

        {/* Email / password SECOND */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 20px",
                marginBottom: "2px",
                fontSize: "16px",
                fontFamily: "basic-sans",
                borderRadius: "50px",
                outline: "none",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 20px",
                marginBottom: "0px",
                fontSize: "16px",
                fontFamily: "basic-sans",
                borderRadius: "50px",
                outline: "none",
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              onClick={() => handleEmailPasswordSignIn(email, password)}
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: theme.colors.darkBlue,
                border: `1px solid ${theme.colors.darkGray}`,
                color: theme.colors.white,
                borderRadius: "50px",
                padding: "10px 30px",
                fontFamily: "basic-sans",
                fontWeight: "bold",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.colors.green,
                  border: `1px solid ${theme.colors.green}`,
                  boxShadow: "none",
                },
              }}
              disabled={loading}
            >
              {loading ? "Signing In..." : "Sign In with Email"}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", mt: 0.5, mb: 1 }}>
              <Link
                component="button"
                underline="hover"
                onClick={() => onForgotPassword && onForgotPassword(email)}
                disabled={loading}
                sx={{
                  fontFamily: "basic-sans",
                  fontSize: "0.9rem",
                  color: theme.colors.red,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                  fontWeight: "bold",
                  mb: 3,
                }}
              >
                Forgot your password?
              </Link>
            </Box>

            {/* CTA to email signup that preserves `from` */}
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" sx={{ color: "#ddd" }}>
                Don’t have Apple or Google?{" "}
                <Link
                  component={RouterLink}
                  to="/challenge/signup"
                  state={{ from }}
                  underline="hover"
                  sx={{ color: theme.colors.amber, fontWeight: 700 }}
                >
                  Sign up with email
                </Link>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AuthComponent;
